/* Presentation only: no submit/click purchase listeners, fetch, or cart writes. */
(() => {
  if (window.pnMotion) return;
  const selector = '.pn-product .product-form__submit, .pn-product .sticky-atc .button, .pn-card .quick-add__submit';
  const states = new WeakMap();
  function init(root = document) {
    const buttons = [...root.querySelectorAll(selector)];
    if (root.matches?.(selector)) buttons.unshift(root);
    buttons.forEach(button => {
      if (states.has(button)) return;
      const label = button.querySelector(':scope > .button__label, :scope > .sticky-atc__label, :scope > .pn-card-quick-label');
      const spinner = button.querySelector(':scope > .loading-overlay__spinner');
      if (!label || !spinner) return;
      const dots = document.createElement('span');
      dots.className = 'pn-motion-dots';
      dots.setAttribute('aria-hidden', 'true');
      for (let i = 0; i < 3; i++) dots.append(document.createElement('span'));
      spinner.append(dots);
      button.dataset.pnMotionButton = '';
      const state = { loading: button.classList.contains('loading') };
      states.set(button, state);
      new MutationObserver(() => {
        const loading = button.classList.contains('loading');
        if (loading === state.loading) return;
        state.loading = loading;
        button.dataset.pnMotionState = loading ? 'loading' : 'restoring';
      }).observe(button, { attributes: true, attributeFilter: ['class'] });
    });
  }
  window.pnMotion = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('pn:quick-add:loaded', event => init(event.target));
})();

/* Existing editorial interactions only; no dependency on the protected story controller. */
(() => {
  if (window.pnMotionExtras) return;
  window.pnMotionExtras = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const bound = new WeakSet();
  const running = new Set();
  function play(element, frames, options) {
    if (!element || reduced.matches || !element.animate) return null;
    const animation = element.animate(frames, { duration: 300, easing: 'ease', ...options });
    running.add(animation);
    animation.finished.catch(() => {}).finally(() => running.delete(animation));
    return animation;
  }
  reduced.addEventListener('change', () => {
    if (reduced.matches) running.forEach(animation => animation.finish());
  });
  const quickClosings = new WeakMap();
  const finishingQuickClose = new WeakSet();
  window.pnMotion.cancelQuickClose = modal => {
    const pending = quickClosings.get(modal);
    if (!pending) return;
    quickClosings.delete(modal);
    pending.forEach(animation => animation?.cancel());
    modal.removeAttribute('data-pn-motion-closing');
  };
  window.pnMotion.deferQuickClose = (modal, finish) => {
    if (reduced.matches || finishingQuickClose.has(modal)) return false;
    if (quickClosings.has(modal)) return true;
    const panel = modal.querySelector('.quick-add-modal__content');
    if (!panel) return false;
    modal.setAttribute('data-pn-motion-closing','');
    const body = play(modal.querySelector('.quick-add-modal__content-info'), [{opacity:1},{opacity:0}], {duration:150,fill:'forwards'});
    const closing = play(panel, [{clipPath:'inset(0)'},{clipPath:'inset(0)',offset:3/14,easing:'cubic-bezier(.86,0,.07,1)'},{clipPath:'inset(100% 0 0)',offset:11/14},{clipPath:'inset(100% 0 0)'}], {duration:700,easing:'linear',fill:'forwards'});
    quickClosings.set(modal,[body,closing]);
    closing.finished.then(() => {
      if (quickClosings.get(modal)?.[1] !== closing) return;
      quickClosings.delete(modal);
      finishingQuickClose.add(modal);
      finish(); // Calls the original hide once, retaining modalClosed and focus restoration.
      finishingQuickClose.delete(modal);
      modal.removeAttribute('data-pn-motion-closing');
      body?.cancel();
      closing.cancel();
    }).catch(() => {});
    return true;
  };
  function each(root, selector, setup) {
    root.querySelectorAll(selector).forEach(element => {
      if (bound.has(element)) return;
      bound.add(element);
      setup(element);
    });
  }
  function init(root = document) {
    each(root, '.pn-product sticky-atc[data-after-scroll="true"]', sticky => {
      const sync = () => { sticky.dataset.pnMotionSticky = sticky.style.transform === 'none' ? 'visible' : 'hidden'; };
      // The existing controller still decides WHEN to show the bar.
      new MutationObserver(sync).observe(sticky,{attributes:true,attributeFilter:['style']});
      sync();
    });
    // Observe the existing timeline selection. Its native arrows/dots remain sole owners.
    each(root, '.pn-home-timeline .timeline', timeline => {
      const slides = [...timeline.querySelectorAll('.timeline__slide')];
      const dots = [...timeline.querySelectorAll('.timeline__nav-item')];
      let current = Math.max(0, dots.findIndex(dot => dot.getAttribute('aria-current') === 'true'));
      let outgoing = null, fade = null, enter = null;
      const clean = () => {
        fade?.cancel(); enter?.cancel();
        if (outgoing) {
          outgoing.element.style.cssText = outgoing.style;
          outgoing.element.hidden = true;
          outgoing.element.removeAttribute('aria-hidden');
          outgoing.element.inert = false;
          outgoing = null;
        }
      };
      new MutationObserver(() => {
        const next = dots.findIndex(dot => dot.getAttribute('aria-current') === 'true');
        if (next < 0 || next === current) return;
        clean();
        const old = slides[current], incoming = slides[next];
        current = next;
        // A rapid reversal may select the outgoing layer just cleaned above.
        incoming.hidden = false;
        incoming.style.display = 'grid';
        if (reduced.matches) return;
        outgoing = { element: old, style: old.style.cssText };
        old.hidden = false;
        old.inert = true;
        old.setAttribute('aria-hidden', 'true');
        Object.assign(old.style, {display:'grid',position:'absolute',inset:'0',width:'100%',pointerEvents:'none'});
        fade = play(old, [{opacity:1},{opacity:0}], {duration:300});
        enter = play(incoming, [{opacity:0},{opacity:1}], {duration:300});
        const last = outgoing;
        fade?.finished.then(() => { if (outgoing === last) clean(); }).catch(() => {});
      }).observe(timeline.querySelector('.timeline__nav'), {subtree:true,attributes:true,attributeFilter:['aria-current']});
    });
    // Native details retains keyboard semantics; only these product disclosures are animated.
    each(root, '.pn-product details.product-info__accordion', details => {
      const summary = details.querySelector(':scope > summary');
      const content = details.querySelector(':scope > .accordion__content');
      if (!summary || !content) return;
      let wanted = details.open, size = null, text = null, version = 0;
      summary.addEventListener('click', event => {
        event.preventDefault();
        wanted = !wanted;
        const token = ++version;
        const from = details.getBoundingClientRect().height;
        size?.cancel(); text?.cancel();
        details.open = true;
        const box = getComputedStyle(details);
        const collapsed = summary.getBoundingClientRect().height + parseFloat(box.borderTopWidth) + parseFloat(box.borderBottomWidth) + parseFloat(box.paddingTop) + parseFloat(box.paddingBottom);
        const to = wanted ? details.getBoundingClientRect().height : collapsed;
        summary.setAttribute('aria-expanded', String(wanted));
        details.toggleAttribute('data-pn-motion-closing', !wanted);
        const finish = () => {
          if (version !== token) return;
          details.open = wanted;
          details.style.removeProperty('overflow');
          details.removeAttribute('data-pn-motion-closing');
        };
        if (reduced.matches) { finish(); return; }
        details.style.overflow = 'hidden';
        size = play(details, [{height:from+'px',easing:'ease'},{height:to+'px',offset:wanted?5/6:1},{height:to+'px'}], {duration:wanted?300:250,easing:'linear'});
        text = play(content, wanted
          ? [{opacity:0,transform:'translateY(4px)'},{opacity:0,transform:'translateY(4px)',offset:.5,easing:'ease'},{opacity:1,transform:'translateY(0)'}]
          : [{opacity:1,easing:'ease'},{opacity:0,offset:.6},{opacity:0}], {duration:wanted?300:250,easing:'linear'});
        size?.finished.then(finish).catch(() => {});
      });
    });
    each(root, '.pn-parity-header .an-navigation--desktop details', details => {
      const list = details.querySelector(':scope > ul');
      if (!list) return;
      details.dataset.pnMotionDisclosure = 'desktop';
      let animations = [];
      details.addEventListener('toggle', () => {
        animations.forEach(a => a?.cancel()); animations = [];
        if (reduced.matches) return;
        animations.push(play(list, details.open ? [{opacity:0},{opacity:1}] : [{opacity:1},{opacity:0}], {duration:200}));
        if (details.open) [...list.children].forEach((item,i) => animations.push(play(item,
          [{opacity:0,transform:'translateY(-10px)'},{opacity:1,transform:'translateY(0)'}],
          {duration:200,delay:100+i*25,fill:'backwards'})));
      });
    });
    each(root, '.pn-parity-header .an-mobile-menu', menu => {
      const panel = menu.querySelector('.an-mobile-panel');
      if (!panel) return;
      menu.dataset.pnMotionDisclosure = 'mobile';
      let animations = [];
      menu.addEventListener('toggle', () => {
        animations.forEach(a => a?.cancel()); animations = [];
        if (reduced.matches) return;
        const closed = 'inset(100% 0 0 round 8px)', open = 'inset(0 round 8px)';
        const start = menu.open ? closed : open, end = menu.open ? open : closed;
        animations.push(play(panel, [{clipPath:start},{clipPath:start,offset:3/14,easing:'cubic-bezier(.86,0,.07,1)'},{clipPath:end,offset:11/14},{clipPath:end}], {duration:700,easing:'linear'}));
        [...panel.children].forEach(child => animations.push(play(child, menu.open
          ? [{opacity:0},{opacity:0,offset:11/14,easing:'ease'},{opacity:1}]
          : [{opacity:1,easing:'ease'},{opacity:0,offset:3/14},{opacity:0}], {duration:700,easing:'linear'})));
      });
    });
    each(root, '.pn-parity-header .an-navigation--mobile', nav => {
      const body = nav.closest('.pn-home-menu-body');
      if (!body) return;
      let snapshot = null, animations = [], version = 0;
      const clean = () => {
        animations.forEach(animation => animation?.cancel()); animations = [];
        snapshot?.remove(); snapshot = null;
        body.style.removeProperty('overflow');
        body.style.removeProperty('position');
      };
      nav.querySelectorAll(':scope > ul > li > details').forEach(details => {
        const summary = details.querySelector(':scope > summary');
        // Capture the outgoing appearance only. Native details still owns the toggle.
        summary.addEventListener('click', () => {
          ++version; clean();
          if (reduced.matches) return;
          snapshot = nav.cloneNode(true);
          snapshot.inert = true;
          snapshot.setAttribute('aria-hidden','true');
          snapshot.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
          const from = nav.getBoundingClientRect(), area = body.getBoundingClientRect();
          Object.assign(snapshot.style,{position:'absolute',top:(from.top-area.top+body.scrollTop)+'px',left:(from.left-area.left)+'px',width:from.width+'px',pointerEvents:'none',margin:'0'});
        });
        details.addEventListener('toggle', () => {
          const token = ++version;
          if (reduced.matches || !nav.closest('.an-mobile-menu')?.open) { clean(); return; }
          body.style.position = 'relative'; body.style.overflow = 'hidden';
          const forward = details.open;
          if (snapshot) {
            body.append(snapshot);
            animations.push(play(snapshot,[{opacity:1,transform:'translateX(0)'},{opacity:0,transform:`translateX(${forward?'-':''}100%)`}],{duration:300,fill:'forwards'}));
          }
          animations.push(play(nav,[{opacity:0,transform:`translateX(${forward?'':'-'}100%)`},{opacity:1,transform:'translateX(0)'}],{duration:300}));
          if (forward) [...details.querySelector(':scope > ul').children].forEach((item,i) => animations.push(play(item,[{opacity:0,transform:'translateY(-10px)'},{opacity:1,transform:'translateY(0)'}],{duration:200,delay:250+i*25,fill:'backwards'})));
          Promise.all(animations.filter(Boolean).map(animation => animation.finished.catch(() => {}))).then(() => { if (version === token) clean(); });
        });
      });
      nav.closest('.an-mobile-menu')?.addEventListener('toggle', event => {
        if (!event.currentTarget.open) { ++version; clean(); }
      });
    });
    each(root, '.pn-home-hero .content-over-media', hero => {
      hero.querySelectorAll(':scope > img').forEach(img => {
        const reveal = () => play(img, [{opacity:0,transform:'scale(1.05)'},{opacity:1,transform:'scale(1)'}], {duration:300});
        if (img.complete) reveal(); else img.addEventListener('load', reveal, {once:true});
      });
      hero.querySelectorAll('.prose > p, .prose .button').forEach(text => play(text,[{opacity:0},{opacity:1}],{duration:300,delay:300,fill:'backwards'}));
    });
    each(root, '.pn-card', card => {
      if (reduced.matches || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        const siblings = [...card.parentElement.parentElement.querySelectorAll('.pn-card')];
        const column = siblings.filter(el => Math.abs(el.getBoundingClientRect().top-card.getBoundingClientRect().top)<5).indexOf(card);
        play(card,[{opacity:0,transform:'translateY(15px)'},{opacity:1,transform:'translateY(0)'}],{duration:350,delay:Math.max(0,column)*80,fill:'backwards'});
      }, {threshold:0.05});
      observer.observe(card);
    });
    // Modal state/rendering remains in quick-add.js. Reveal only after native open.
    each(root, 'quick-add-modal', modal => {
      const panel = modal.querySelector('.quick-add-modal__content');
      if (!panel) return;
      new MutationObserver(() => {
        if (!modal.hasAttribute('open')) return;
        play(panel,[{clipPath:'inset(100% 0 0)'},{clipPath:'inset(100% 0 0)',offset:3/14,easing:'cubic-bezier(.86,0,.07,1)'},{clipPath:'inset(0)',offset:11/14},{clipPath:'inset(0)'}],{duration:700,easing:'linear'});
        play(modal.querySelector('.quick-add-modal__content-info'),[{opacity:0},{opacity:0,offset:11/14,easing:'ease'},{opacity:1}],{duration:700,easing:'linear'});
      }).observe(modal,{attributes:true,attributeFilter:['open']});
    });
    each(root, 'cart-drawer.drawer', drawer => {
      let open = drawer.classList.contains('active');
      let started = -Infinity, panel = null, animations = [];
      drawer.dataset.pnMotionDrawer = '';
      const sync = () => {
        const next = drawer.classList.contains('active');
        const node = drawer.querySelector('.drawer__inner');
        const changed = next !== open;
        if (!changed && node === panel) return;
        if (changed) { open = next; started = performance.now(); }
        panel = node;
        animations.forEach(a => a?.cancel()); animations = [];
        const elapsed = performance.now()-started;
        if (!panel || reduced.matches || elapsed >= 700) return;
        const start = open ? 'inset(100% 0 0)' : 'inset(0)';
        const end = open ? 'inset(0)' : 'inset(100% 0 0)';
        animations.push(play(panel,[{clipPath:start},{clipPath:start,offset:3/14,easing:'cubic-bezier(.86,0,.07,1)'},{clipPath:end,offset:11/14},{clipPath:end}],{duration:700,easing:'linear'}));
        [...panel.children].forEach(child => animations.push(play(child, open
          ? [{opacity:0},{opacity:0,offset:11/14,easing:'ease'},{opacity:1}]
          : [{opacity:1,easing:'ease'},{opacity:0,offset:3/14},{opacity:0}], {duration:700,easing:'linear'})));
        // Gift/quantity rendering may replace the panel: continue, never replay the opening.
        animations.forEach(a => { if (a) a.currentTime = elapsed; });
      };
      new MutationObserver(sync).observe(drawer,{attributes:true,attributeFilter:['class'],childList:true,subtree:true});
      sync();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('pn:quick-add:loaded', event => init(event.target));
})();
